import { Injectable, inject } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { filter, withLatestFrom } from 'rxjs';
import { selectTableColumnsFeature } from 'src/app/store/app.selectors';
import { AdminUserPreferenceScope, PreferenceValue } from '../../api/types/admin-user-preference';
import { TableColumnDelta } from '../../api/types/interfaces';
import { PreferencesService } from '../../services/preferences/preferences.service';
import {
  DATA_TABLE_PREFERENCE,
  diffTableColumns,
  flattenTableColumns,
  tableColumnsFromPreferences
} from './table-columns-preferences.util';
import * as TableColumnsActions from './table-columns.actions';

const GLOBAL = AdminUserPreferenceScope.Global;

/**
 * Every action that changes a layout: the per-table editor actions (each reduced to a delta by
 * extractDelta, reset included) and the two width actions. Derived from the actions module, so a
 * table added there is persisted with no change here. The hydration action is the one exception.
 */
const LAYOUT_ACTION_TYPES = new Set<string>(
  Object.values(TableColumnsActions)
    .filter((creator): creator is typeof creator & { type: string } => typeof creator === 'function' && 'type' in creator)
    .map((creator) => creator.type)
    .filter((type) => type !== TableColumnsActions.tableColumnsHydrated.type)
);

/**
 * NBO-2516: column layouts live on the account — one `dataTable` preference per table — and the
 * tableColumns slice is a copy of them. It replaces the localStorageSync of the browser-wide
 * `tableColumns` key, which held one machine's layouts for whoever signed in.
 *
 * - Read: the slice is replaced (`tableColumnsHydrated`) whenever PreferencesService reports a
 *   `dataTable` change — boot from the cache, login from the server, another tab, a user switch.
 * - Write: after every layout action, the slice is diffed against what was last hydrated or
 *   written, and each changed table is one `set` (a reset is a removal). Only layout actions write,
 *   never a hydration and never the logout reset.
 *
 * Constructed by AppInitializationService, before routing, so the first listing already reads the
 * user's layouts.
 */
@Injectable({ providedIn: 'root' })
export class TableColumnsPreferencesService {
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);
  private readonly preferences = inject(PreferencesService);

  /** The layouts as last hydrated or written — what the next diff is measured against. */
  private baseline: Record<string, TableColumnDelta> = {};

  constructor() {
    this.hydrate();
    this.preferences.changes$
      .pipe(filter((types) => types.includes(DATA_TABLE_PREFERENCE)))
      .subscribe(() => this.hydrate());

    this.actions
      .pipe(
        filter((action: Action) => LAYOUT_ACTION_TYPES.has(action.type)),
        withLatestFrom(this.store.select(selectTableColumnsFeature))
      )
      .subscribe(([, slice]) => this.persist(slice));
  }

  private hydrate(): void {
    // No known user yet (a machine nobody signed in on): the code defaults.
    const tree = this.preferences.hasUser()
      ? tableColumnsFromPreferences(this.preferences.values(DATA_TABLE_PREFERENCE, GLOBAL))
      : {};
    this.baseline = flattenTableColumns(tree);
    this.store.dispatch(TableColumnsActions.tableColumnsHydrated({ tree }));
  }

  private persist(slice: unknown): void {
    const current = flattenTableColumns(slice);
    const { changed, removed } = diffTableColumns(this.baseline, current);
    for (const [path, delta] of Object.entries(changed)) {
      this.preferences.set(DATA_TABLE_PREFERENCE, path, GLOBAL, delta as unknown as PreferenceValue);
    }
    for (const path of removed) this.preferences.remove(DATA_TABLE_PREFERENCE, path, GLOBAL);
    this.baseline = current;
  }
}
